'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Chip, Skeleton } from '@common/components';
import { cn } from '@core/utils';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { usePathname, useSearchParams } from 'next/navigation';
import React, { useCallback, useRef, useState } from 'react';
import { AuthInfiniteQueryOptions, AuthQueryOptions } from '@/api';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { CustomLink } from '@/app/_components/CustomLink';
import { PageNavigation } from '@/components/client';
import { ROUTES, SUB_DOMAIN } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { PointHistoryCard } from './_components/PointHistoryCard';
import type { HistoryTypeParamValue } from './constants';
import { ACTIVITY_DETAIL_TYPE_CD_MAP } from './constants';
const INITIAL_PAGE = 1;
const PAGE_SIZE = 50;
const PointsMainPage = () => {
  const $ = _c(93);
  const {
    replace
  } = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams?.get(TYPE_QUERY_STRING_KEY) ?? null;
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const historyTypeParam = t0;
  const isBottomButtonShow = useDeviceInfoStore(_temp);
  const observerElemRef = useRef(null);
  const [typeFilter, setTypeFilter] = useState(historyTypeParam as HistoryTypeParamValue | null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ...AuthQueryOptions["get:/wello/v2/auth/point"](),
      placeholderData: keepPreviousData,
      staleTime: 0,
      select: _temp2
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data: pointData
  } = useQuery(t1);
  const totalPoints = pointData?.totalPoints ?? 0;
  let t2;
  if ($[3] !== typeFilter) {
    t2 = AuthInfiniteQueryOptions["get:/wello/v2/auth/point/history"]({
      query: {
        activityType: typeFilter ? ACTIVITY_DETAIL_TYPE_CD_MAP[typeFilter] : undefined,
        page: INITIAL_PAGE,
        size: PAGE_SIZE
      }
    });
    $[3] = typeFilter;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = {
      ...t2,
      getNextPageParam: _temp3,
      initialPageParam: INITIAL_PAGE,
      staleTime: 0,
      gcTime: 0
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    isLoading: historyInfiniteLoading,
    data: historyInfiniteListData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery(t3);
  let historyList;
  let observeElement;
  let t10;
  let t11;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[7] !== fetchNextPage || $[8] !== hasNextPage || $[9] !== historyInfiniteListData?.pages || $[10] !== historyInfiniteLoading || $[11] !== pathname || $[12] !== replace || $[13] !== searchParams || $[14] !== totalPoints || $[15] !== typeFilter) {
    historyList = historyInfiniteListData?.pages.flatMap(_temp4);
    const isEmpty = historyInfiniteListData?.pages[0]?.context?.totalCount === 0;
    let t12;
    bb0: switch (typeFilter) {
      case "usage":
        {
          t12 = "\uC0AC\uC6A9 \uB0B4\uC5ED\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.";
          break bb0;
        }
      case "accumulation":
        {
          t12 = "\uC801\uB9BD \uB0B4\uC5ED\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.";
          break bb0;
        }
      case "reclaimed":
        {
          t12 = "\uCC28\uAC10 \uB0B4\uC5ED\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.";
          break bb0;
        }
      default:
        {
          t12 = "\uD3EC\uC778\uD2B8 \uB0B4\uC5ED\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.";
        }
    }
    const emptyText = t12;
    let t13;
    if ($[26] !== fetchNextPage || $[27] !== hasNextPage) {
      t13 = node => {
        if (observerElemRef.current) {
          observerElemRef.current.disconnect();
        }
        if (!node) {
          return;
        }
        observerElemRef.current = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting && hasNextPage) {
            fetchNextPage();
          }
        });
        observerElemRef.current.observe(node);
      };
      $[26] = fetchNextPage;
      $[27] = hasNextPage;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    observeElement = t13;
    let t14;
    if ($[29] !== pathname || $[30] !== replace || $[31] !== searchParams) {
      t14 = (value, checked) => {
        const params = new URLSearchParams(searchParams?.toString());
        if (checked) {
          if (value) {
            setTypeFilter(value);
            params.set(TYPE_QUERY_STRING_KEY, value);
          } else {
            setTypeFilter(null);
            params.delete(TYPE_QUERY_STRING_KEY);
          }
          replace(`${pathname}?${params.toString()}`);
        }
      };
      $[29] = pathname;
      $[30] = replace;
      $[31] = searchParams;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    const handleFilterChange = t14;
    t10 = "mx-auto flex w-full max-w-screen-sm flex-1 flex-col";
    if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <PageNavigation />;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    t6 = "flex grow flex-col overflow-auto pb-120 pt-20";
    let t15;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <p className="text-Gray-700 typo-body4">보유 포인트</p>;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== totalPoints) {
      t16 = totalPoints.toLocaleString();
      $[35] = totalPoints;
      $[36] = t16;
    } else {
      t16 = $[36];
    }
    let t17;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <span className="ml-4">P</span>;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    if ($[38] !== t16) {
      t7 = <div className="mb-28 flex justify-between px-20"><div className="flex flex-col gap-4">{t15}<p className="font-700 typo-h3">{t16}{t17}</p></div></div>;
      $[38] = t16;
      $[39] = t7;
    } else {
      t7 = $[39];
    }
    let t18;
    let t19;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <li className="list-disc typo-caption1">부정한 방법으로 포인트 획득 시, 포인트는 전부 회수돼요.</li>;
      t19 = <li className="list-disc typo-caption1">웰포인트는 적립일로부터 60일까지 사용 가능해요.</li>;
      $[40] = t18;
      $[41] = t19;
    } else {
      t18 = $[40];
      t19 = $[41];
    }
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <div className="mb-28 px-20"><ul className="flex list-none flex-col px-20 text-Gray-700">{t18}{t19}<li className="list-disc typo-caption1">포인트 사용에 대한 상세 안내는{" "}<CustomLink className="underline" href={ROUTES.POINTS_GUIDELINES.pathname}>여기서</CustomLink>{" "}확인하세요.</li></ul></div>;
      $[42] = t8;
    } else {
      t8 = $[42];
    }
    const t20 = typeFilter === null;
    let t21;
    if ($[43] !== handleFilterChange) {
      t21 = checked_0 => handleFilterChange(null, checked_0);
      $[43] = handleFilterChange;
      $[44] = t21;
    } else {
      t21 = $[44];
    }
    let t22;
    if ($[45] !== t20 || $[46] !== t21) {
      t22 = <Chip checked={t20} className="px-10 py-5 typo-body4" onCheckedChange={t21}>전체</Chip>;
      $[45] = t20;
      $[46] = t21;
      $[47] = t22;
    } else {
      t22 = $[47];
    }
    const t23 = typeFilter === "accumulation";
    let t24;
    if ($[48] !== handleFilterChange) {
      t24 = checked_1 => handleFilterChange("accumulation", checked_1);
      $[48] = handleFilterChange;
      $[49] = t24;
    } else {
      t24 = $[49];
    }
    let t25;
    if ($[50] !== t23 || $[51] !== t24) {
      t25 = <Chip checked={t23} className="px-10 py-5 typo-body4" onCheckedChange={t24}>적립내역</Chip>;
      $[50] = t23;
      $[51] = t24;
      $[52] = t25;
    } else {
      t25 = $[52];
    }
    const t26 = typeFilter === "usage";
    let t27;
    if ($[53] !== handleFilterChange) {
      t27 = checked_2 => handleFilterChange("usage", checked_2);
      $[53] = handleFilterChange;
      $[54] = t27;
    } else {
      t27 = $[54];
    }
    let t28;
    if ($[55] !== t26 || $[56] !== t27) {
      t28 = <Chip checked={t26} className="px-10 py-5 typo-body4" onCheckedChange={t27}>사용내역</Chip>;
      $[55] = t26;
      $[56] = t27;
      $[57] = t28;
    } else {
      t28 = $[57];
    }
    const t29 = typeFilter === "reclaimed";
    let t30;
    if ($[58] !== handleFilterChange) {
      t30 = checked_3 => handleFilterChange("reclaimed", checked_3);
      $[58] = handleFilterChange;
      $[59] = t30;
    } else {
      t30 = $[59];
    }
    let t31;
    if ($[60] !== t29 || $[61] !== t30) {
      t31 = <Chip checked={t29} className="px-10 py-5 typo-body4" onCheckedChange={t30}>차감내역</Chip>;
      $[60] = t29;
      $[61] = t30;
      $[62] = t31;
    } else {
      t31 = $[62];
    }
    if ($[63] !== t22 || $[64] !== t25 || $[65] !== t28 || $[66] !== t31) {
      t9 = <div className="flex gap-6 px-20 py-8">{t22}{t25}{t28}{t31}</div>;
      $[63] = t22;
      $[64] = t25;
      $[65] = t28;
      $[66] = t31;
      $[67] = t9;
    } else {
      t9 = $[67];
    }
    if ($[68] !== isEmpty) {
      t4 = cn("relative", {
        "grow flex flex-col justify-center items-center": isEmpty
      });
      $[68] = isEmpty;
      $[69] = t4;
    } else {
      t4 = $[69];
    }
    t5 = historyInfiniteLoading ? <div className="flex flex-col gap-10 p-20"><Skeleton /><Skeleton className="h-50" /></div> : isEmpty ? <div className="text-Gray-600 typo-body3">{emptyText}</div> : historyList?.map((history, index) => {
      const prevYear = historyList[index - 1]?.year;
      const showYear = index === 0 || history.year !== prevYear;
      return <React.Fragment key={history.pointActivityId}>{showYear ? <div className={cn("px-20 pb-4 pt-32", {
          "pt-16": index === 0
        })}><p className="font-600 text-Gray-800 typo-caption1">{history.year}</p></div> : null}<PointHistoryCard contentIdx={history.contentIdIdx} date={history.activityDate ?? "-"} description={history.activityDetailTypeString} point={history.points} pointActivity={history.activityDetailTypeCd} title={history.activityTypeString} /></React.Fragment>;
    });
    $[7] = fetchNextPage;
    $[8] = hasNextPage;
    $[9] = historyInfiniteListData?.pages;
    $[10] = historyInfiniteLoading;
    $[11] = pathname;
    $[12] = replace;
    $[13] = searchParams;
    $[14] = totalPoints;
    $[15] = typeFilter;
    $[16] = historyList;
    $[17] = observeElement;
    $[18] = t10;
    $[19] = t11;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    historyList = $[16];
    observeElement = $[17];
    t10 = $[18];
    t11 = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
    t9 = $[25];
  }
  let t12;
  if ($[70] !== historyList || $[71] !== isFetchingNextPage) {
    t12 = historyList && isFetchingNextPage ? <div className="p-20"><Skeleton /></div> : null;
    $[70] = historyList;
    $[71] = isFetchingNextPage;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  let t13;
  if ($[73] !== observeElement) {
    t13 = <div ref={observeElement} className="pointer-events-none absolute inset-x-0 bottom-0 h-[50vh]" />;
    $[73] = observeElement;
    $[74] = t13;
  } else {
    t13 = $[74];
  }
  let t14;
  if ($[75] !== t12 || $[76] !== t13 || $[77] !== t4 || $[78] !== t5) {
    t14 = <ul className={t4}>{t5}{t12}{t13}</ul>;
    $[75] = t12;
    $[76] = t13;
    $[77] = t4;
    $[78] = t5;
    $[79] = t14;
  } else {
    t14 = $[79];
  }
  let t15;
  if ($[80] !== isBottomButtonShow) {
    t15 = isBottomButtonShow ? <BottomNavPortal><footer className="flex w-full gap-8 px-20 py-16"><Button asChild={true} className="w-full flex-1"><CustomLink href={ROUTES.ATTENDANCE_DAILY_CHECK.pathname}>출석체크하기</CustomLink></Button><Button asChild={true} className="w-full flex-1" theme="sub-fill"><CustomLink href={ROUTES.POINT_MARKET.pathname}>포인트 사용하기</CustomLink></Button></footer></BottomNavPortal> : null;
    $[80] = isBottomButtonShow;
    $[81] = t15;
  } else {
    t15 = $[81];
  }
  let t16;
  if ($[82] !== t14 || $[83] !== t15 || $[84] !== t6 || $[85] !== t7 || $[86] !== t8 || $[87] !== t9) {
    t16 = <article className={t6}>{t7}{t8}{t9}{t14}{t15}</article>;
    $[82] = t14;
    $[83] = t15;
    $[84] = t6;
    $[85] = t7;
    $[86] = t8;
    $[87] = t9;
    $[88] = t16;
  } else {
    t16 = $[88];
  }
  let t17;
  if ($[89] !== t10 || $[90] !== t11 || $[91] !== t16) {
    t17 = <div className={t10}>{t11}{t16}</div>;
    $[89] = t10;
    $[90] = t11;
    $[91] = t16;
    $[92] = t17;
  } else {
    t17 = $[92];
  }
  return t17;
};
export default PointsMainPage;
const TYPE_QUERY_STRING_KEY = 'type';
function _temp(state) {
  return state.subDomain !== SUB_DOMAIN.KB_PAY && state.subDomain !== SUB_DOMAIN.TOSS;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return {
    totalPoints: context?.totalPoints,
    debitablePoints: context?.debitablePoints,
    memberOAuthId: context?.memberOAuthId
  };
}
function _temp3(lastPage, allPages) {
  if (lastPage.context && lastPage.context.contents.length > 0) {
    return allPages.length + 1;
  }
  return null;
}
function _temp4(t0) {
  const {
    context: context_0
  } = t0;
  return context_0?.contents ?? [];
}